/* ExposureDispositions.js */

"use strict";
// Presentation-layer vocabulary for the exposure surface: the single place where a stored enum
// becomes words an operator reads. Used by the Exposure tab, drawer, hover popup, summary strip,
// CSV export and the justification bundle.
//
// THE RULE: any stored value that reaches a human passes through this file. Storage keeps the
// enum (filtering and CSV depend on it); presentation resolves it here. `decidedAt` shipped
// without that treatment and rendered as "Decided on: predicate" in production — a field labelled
// for humans showing an engine token.

// ---------------------------------------------------------------------------
// Disposition
// ---------------------------------------------------------------------------

const DISPOSITION_LABELS = {
    red: "Affected",
    yellow: "Potentially Affected",
    green: "Not Affected",
    inconclusive: "Inconclusive",
};

// Reason codes that make a red WEAKER than the plain word suggests.
//
// A version-only red matched the advisory's affected release list and nothing else: the predicate
// carried no feature gate, so no configuration was consulted. On the reference device 21 of 22
// reds are this, against 1 that was confirmed against the running config — and both said
// "Affected" in identical type, which is why the column stopped being useful.
//
// The qualifier says what WE did ("matched on version alone"), not what the advisory is. That
// distinction matters: an empty required-tag list means the extractor produced no feature gate,
// which is NOT the same as the advisory having no precondition. Wording it as a claim about the
// advisory would assert something no one established.
const VERSION_ONLY_REASONS = new Set(["VERSION_ONLY_MATCH"]);

// Reason codes that make a verdict STRONGER than the bare word conveys, and that is worth saying
// out loud for the same reason "(version only)" is: an operator triaging a list needs to know which
// verdicts rest on evidence from the device and which rest on a release number alone.
//
// "config confirmed" — the advisory's own determination line is present in this device's running
// configuration. "config proven" — the feature is demonstrably off, either because an explicit
// disable line is present or because the advisory states a negative result clears the device.
const CONFIG_QUALIFIED_REASONS = {
    CONFIG_MATCH_CONFIRMED: "config confirmed",
    CONFIG_FEATURE_DISABLED: "config proven",
    CONFIG_DEFAULT_OFF_ABSENT: "config proven",
};

/**
 * Why a disposition carries a qualifier, and which WAY the qualifier points.
 *
 * Lives here rather than in a renderer because this module owns the presentation vocabulary, and
 * because the grid and the drawer both build the same span. The grid grew a local copy of this map
 * while the drawer header shipped with no tooltip at all — one map, two call sites, so the next
 * qualifier added cannot reach only half the UI.
 */
const QUALIFIER_TOOLTIPS = {
    VERSION_ONLY_MATCH:
        "Matched the advisory's affected releases. No feature precondition was checked against this device's configuration.",
    CONFIG_MATCH_CONFIRMED:
        "The advisory's own determination line is present in this device's running configuration.",
    CONFIG_FEATURE_DISABLED: "The running configuration explicitly disables the affected feature.",
    CONFIG_DEFAULT_OFF_ABSENT:
        "The affected feature is not configured, and the advisory states that a negative result clears the device.",
};

/** The tooltip for a qualifier, or "" when the reason code carries none. */
function qualifierTooltip(reasonCode) {
    return (reasonCode && QUALIFIER_TOOLTIPS[reasonCode]) || "";
}

/**
 * Does the qualifier make the verdict STRONGER, or weaker?
 *
 * The styling depends on it and used to assume every qualifier weakened the chip — true when
 * "(version only)" was the only one. Three of the four now mean the opposite: the verdict rests on
 * configuration read off this device, which is the strongest thing the panel can say, and it was
 * rendering in the same muted grey used for "ignore me".
 *
 * @returns {String} A modifier class: "qualifier-strengthens" or "qualifier-weakens".
 */
function qualifierWeight(reasonCode) {
    return reasonCode && CONFIG_QUALIFIED_REASONS[reasonCode]
        ? "qualifier-strengthens"
        : "qualifier-weakens";
}

/**
 * Operator-facing disposition.
 *
 * @param {string} score red|yellow|green|inconclusive, as stored.
 * @param {string} [reasonCode] optional; when it identifies a version-only match the label is
 *   qualified. Omitting it yields the unqualified label, so old call sites stay correct.
 */
function dispositionLabel(score, reasonCode) {
    const normalized = (score || "").trim().toLowerCase();
    const base = DISPOSITION_LABELS[normalized] || (score ? String(score) : "—");
    if (normalized === "red" && reasonCode && VERSION_ONLY_REASONS.has(reasonCode)) {
        return base + " (version only)";
    }
    if (reasonCode && CONFIG_QUALIFIED_REASONS[reasonCode]) {
        return base + " (" + CONFIG_QUALIFIED_REASONS[reasonCode] + ")";
    }
    return base;
}

// ---------------------------------------------------------------------------
// Deciding level
// ---------------------------------------------------------------------------

// Mirrors the backend's LEVEL_LEAD (ExposureScorer). Kept here rather than shipped from the
// scorer because presentation vocabulary lives in the frontend — the same reason `score` stays
// red/green at rest and becomes "Affected"/"Not Affected" here.
//
// 'signature' is deliberately NOT rendered as "signature": this product already uses that word
// for IPS signatures (Advisory.data.ipsSignatures), so the raw token would read as a different
// concept entirely.
const DECIDED_AT_LABELS = {
    predicate: "advisory data",
    signature: "device data",
    vendor: "vendor",
    os: "OS support",
    platform: "hardware",
    module: "hardware module",
    feature: "features",
    // Deliberately NOT "features": a verdict decided by matching a config line but labelled
    // "features" sends the operator to an empty tag list looking for the reason.
    config: "running config",
    version: "version",
    runtime: "runtime state",
};

/** Human phrase for a deciding level. Unknown or absent renders an em dash, never a raw token. */
function decidedAtLabel(value) {
    const normalized = (value || "").trim().toLowerCase();
    return DECIDED_AT_LABELS[normalized] || (value ? String(value) : "—");
}

// ---------------------------------------------------------------------------
// Tag evidence
// ---------------------------------------------------------------------------

// Operator-facing labels for the tag-evidence buckets (backend `tagEvidence`).
//
// Only TWO of the three buckets are ever rendered as a list. `notEvaluated` deliberately has no
// label: it would have to serve two situations needing opposite responses — no config snapshot
// (capture the device and re-run) versus decided earlier at hardware or version (ignore the tag
// list entirely) — and one label cannot carry both. That fact rides the verdict sentence instead,
// where the deciding level supplies the why.
const TAG_EVIDENCE_LABELS = {
    notConfigured: "Not configured",
    notObservable: "Cannot verify",
};

// Appended to a "Cannot verify" list: absence of these tags is not evidence, so the operator is
// told what to do instead of being left to infer it. ONE phrasing, used by every surface — the
// bundle used to retype its own ("no detection rule"), so the same concept read two ways.
const TAG_EVIDENCE_HINTS = {
    notObservable: "no detection rule yet — check these on the device",
};

// Audit-only label. The justification bundle DOES name notEvaluated, because a reader
// reconstructing a decision has to know those tags were never examined — the deciding level on
// the line above supplies the why, which is the context the UI cannot fit next to a list.
const TAG_EVIDENCE_AUDIT_LABELS = {
    notEvaluated: "Not evaluated",
};

// Shown for verdicts persisted before tagEvidence existed. Those rows carry only the flat
// missingTags list, and rendering it under one of the labels above would assert a meaning the
// scorer never established.
const TAG_EVIDENCE_LEGACY_LABEL = "Feature tags";
const TAG_EVIDENCE_LEGACY_NOTE = "scored before evidence categories were recorded";

// Shown when a verdict carries NO feature evidence at all.
//
// Replaces a rendered "Not configured: —" row, which asserted that a configuration check ran and
// found nothing missing. It had not run. Two situations reach here — the predicate carried no
// feature gate, or the verdict was decided at hardware/version before tags were consulted — and
// both are honestly described as "no feature check", which is a statement about our evaluation
// rather than about the device or the advisory.
const NO_FEATURE_EVIDENCE_LABEL = "Feature check";
const NO_FEATURE_EVIDENCE_NOTE = "not performed — this verdict rests on the evidence above";

/**
 * Normalize a verdict's tag evidence for rendering.
 *
 * Returns {legacy, sections}. `sections` is a list of {label, tags, hint}; `tags` is empty for the
 * no-evidence section, so callers render the label and hint without a value list.
 */
function tagEvidenceSections(entry) {
    const evidence = entry && entry.tagEvidence;
    if (!evidence) {
        const missing = (entry && entry.missingTags) || [];
        return {
            legacy: true,
            sections: missing.length
                ? [
                      {
                          label: TAG_EVIDENCE_LEGACY_LABEL,
                          tags: missing,
                          hint: TAG_EVIDENCE_LEGACY_NOTE,
                      },
                  ]
                : [],
        };
    }
    const sections = [];
    ["notConfigured", "notObservable"].forEach((bucket) => {
        const tags = evidence[bucket] || [];
        if (tags.length) {
            sections.push({
                label: TAG_EVIDENCE_LABELS[bucket],
                tags: tags,
                hint: TAG_EVIDENCE_HINTS[bucket] || null,
            });
        }
    });
    if (!sections.length) {
        sections.push({
            label: NO_FEATURE_EVIDENCE_LABEL,
            tags: [],
            hint: NO_FEATURE_EVIDENCE_NOTE,
        });
    }
    return {legacy: false, sections: sections};
}

module.exports = {
    DISPOSITION_LABELS,
    CONFIG_QUALIFIED_REASONS,
    QUALIFIER_TOOLTIPS,
    qualifierTooltip,
    qualifierWeight,
    dispositionLabel,
    DECIDED_AT_LABELS,
    decidedAtLabel,
    TAG_EVIDENCE_LABELS,
    TAG_EVIDENCE_AUDIT_LABELS,
    TAG_EVIDENCE_HINTS,
    TAG_EVIDENCE_LEGACY_LABEL,
    TAG_EVIDENCE_LEGACY_NOTE,
    NO_FEATURE_EVIDENCE_LABEL,
    NO_FEATURE_EVIDENCE_NOTE,
    tagEvidenceSections,
};
